import { scrollTraverserStopIntoView } from "_shared/_views/highlights/traverser-stop-scroll";
import { createContext, useContext, useLayoutEffect, useState, useSyncExternalStore } from "react";

/**
 * The class on a lit row or field — whatever a traverser stop lights *other than* its issue notice.
 *
 * It is how the notice learns whether it is the only thing lit, and how the page finds what to scroll
 * to. Both are questions about what is drawn rather than about the data: the row a stop is about is
 * not drawn when the table's search box hides it, and a table's rows are Material React Table's
 * components, not ours, so nothing short of the DOM knows whether the row is on the page.
 */
export const TRAVERSER_STOP_CLASS = "traverser-stop";

/** The class on an issue notice while it is lit — the stop's highlight when nothing else is. */
export const TRAVERSER_STOP_NOTICE_CLASS = "traverser-stop-notice";

/** {@link TRAVERSER_STOP_CLASS} while `lit`, for a `className` prop. */
export function traverserStopClassName(lit: boolean | undefined): string | undefined {
  return lit ? TRAVERSER_STOP_CLASS : undefined;
}

/**
 * How soon after the tab changes the stop is brought back into view. Changes arriving meanwhile wait
 * for the same look rather than putting it off: a tab whose roster is still loading changes for a
 * second or more, and waiting for it to fall still left its notice under the header all that time.
 */
const SETTLE_MS = 150;

/** What says the reader has taken the page over from the traverser. */
const READER_INPUT_EVENTS = ["wheel", "touchmove", "pointerdown", "keydown"] as const;

/** Listened for before anything on the page can stop it, and never in the way of a scroll. */
const READER_INPUT: AddEventListenerOptions = { capture: true, passive: true };

/**
 * One tab's traverser stops: whether anything but the issue notice is lit, and the scroll to the stop.
 *
 * Attached to the element the tab draws into, it watches that subtree for {@link TRAVERSER_STOP_CLASS}.
 * The notice reads {@link stopHasCounterpart} through {@link useTraverserStopHasCounterpart}, and the
 * page asks for a scroll with {@link requestScroll} each time it arrives at a stop.
 */
export class TraverserStopScope {
  /** A ref callback for the element the tab draws into. */
  readonly attach = (element: HTMLElement | null): void => {
    this.observer?.disconnect();
    this.observer = undefined;
    this.element = element;
    if (!element) this.stopKeepingStopInView();
    if (element && typeof MutationObserver !== "undefined") {
      this.observer = new MutationObserver(() => this.tabChanged());
      this.observer.observe(element, {
        attributeFilter: ["class"],
        attributes: true,
        childList: true,
        subtree: true,
      });
    }
    // The page can arrive before the tab has an element to draw into — a draft does, until its builder
    // loads — and the stop is looked for again once it has one.
    this.tabChanged();
  };

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Whether the stop lights a row or field of its own in this tab. */
  readonly stopHasCounterpart = (): boolean => this.lit;

  /**
   * Scrolls to the stop the page has just arrived at — its lit row or field, and failing that its lit
   * notice — and keeps it in view until the reader takes over.
   *
   * One scroll on arriving is not enough. A stop that switches tab is drawn only once the new tab is,
   * and a row or a notice that comes from a query later still; and whatever loads above the stop after
   * the scroll pushes it back under the page's header. So while the tab keeps changing, the stop is
   * brought back into view each time it settles — which does nothing when it is already in view — and
   * the first scroll, wheel, touch, click or key press of the reader's own ends it.
   */
  requestScroll(): void {
    this.stopKeepingStopInView();
    this.keepingStopInView = true;
    for (const event of READER_INPUT_EVENTS) window.addEventListener(event, this.readerTookOver, READER_INPUT);
    this.revealStop();
  }

  /** Stops keeping the last stop in view: the page is on its way to another. */
  cancelScroll(): void {
    this.stopKeepingStopInView();
  }

  private element: HTMLElement | null = null;
  private observer: MutationObserver | undefined;
  private readonly listeners = new Set<() => void>();
  private lit = false;
  private keepingStopInView = false;
  private settleTimer: ReturnType<typeof setTimeout> | undefined;
  private revealedStop: Element | null = null;

  private readonly readerTookOver = (): void => this.stopKeepingStopInView();

  private tabChanged(): void {
    this.refreshLit();
    if (!this.keepingStopInView) return;
    // A stop drawn just now is brought into view at once — the page is often still busy drawing the
    // rest of the tab, and a timer would wait behind all of it. Anything else waits for the look.
    if (this.stop() !== this.revealedStop) {
      this.revealStop();
      return;
    }
    if (this.settleTimer !== undefined) return;
    this.settleTimer = setTimeout(() => {
      this.settleTimer = undefined;
      this.revealStop();
    }, SETTLE_MS);
  }

  private refreshLit(): void {
    const lit = Boolean(this.counterpart());
    if (lit === this.lit) return;
    this.lit = lit;
    for (const listener of this.listeners) listener();
  }

  private revealStop(): void {
    const stop = this.stop();
    this.revealedStop = stop;
    if (stop) scrollTraverserStopIntoView(stop);
  }

  /** What the stop lights: its row or field, or failing that its notice. */
  private stop(): Element | null {
    return this.counterpart() ?? this.element?.querySelector(`.${TRAVERSER_STOP_NOTICE_CLASS}`) ?? null;
  }

  private stopKeepingStopInView(): void {
    this.keepingStopInView = false;
    this.revealedStop = null;
    clearTimeout(this.settleTimer);
    this.settleTimer = undefined;
    for (const event of READER_INPUT_EVENTS) window.removeEventListener(event, this.readerTookOver, READER_INPUT);
  }

  private counterpart(): Element | null {
    return this.element?.querySelector(`.${TRAVERSER_STOP_CLASS}`) ?? null;
  }
}

/** Outside any page's scope — a tab rendered on its own — a notice is the only thing it knows of. */
const UNSCOPED = new TraverserStopScope();

export const TraverserStopScopeContext = createContext<TraverserStopScope>(UNSCOPED);

/**
 * The page's scope for its tabs, scrolling to the stop each time `arrival` changes — a new tab, or a
 * new arrival at a field, including the one already selected, so "Next" on a lone issue still brings
 * back a row the reader has scrolled away from. An undefined `arrival` is a page still on its way to
 * the stop, and asks for nothing.
 *
 * `attachTraverserStopScope` goes on the element the tab draws into, and `traverserStopScope` into
 * {@link TraverserStopScopeContext} around it. They are handed out apart because the React Compiler
 * reads anything whose member is passed to `ref=` as a ref, and then refuses the context value.
 */
export function useTraverserStopScope(arrival: string | undefined): {
  traverserStopScope: TraverserStopScope;
  attachTraverserStopScope: (element: HTMLElement | null) => void;
} {
  const [traverserStopScope] = useState(() => new TraverserStopScope());
  useLayoutEffect(() => {
    if (arrival === undefined) traverserStopScope.cancelScroll();
    else traverserStopScope.requestScroll();
  }, [traverserStopScope, arrival]);
  return { traverserStopScope, attachTraverserStopScope: traverserStopScope.attach };
}

/** Whether the traverser's stop lights a row or field in the tab this is drawn in. */
export function useTraverserStopHasCounterpart(): boolean {
  const scope = useContext(TraverserStopScopeContext);
  // No server draws this app; the server snapshot is the scope's own, unlit until a tab is attached.
  return useSyncExternalStore(scope.subscribe, scope.stopHasCounterpart, scope.stopHasCounterpart);
}
